import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { boundsCurrencySelector } from '@fcom/common/store/selectors/bounds.selector';
import { cartDataSelector } from '@fcom/booking/store/selectors';
import { Category, FinnairBoundItem, FinnairItineraryItemFlight } from '@fcom/dapi/api/models';

import { isAncillaryBundle, SEAT_BUNDLE_CATEGORIES } from '../../../modules/ancillaries/utils';
import { CartLineItem } from '../../../utils/cart-line-items.utils';
import { isFlight } from '../../../utils/flight.utils';
import {
  cityNameByLocationCodeSelector,
  localCartLineItemsSelector,
  nonSeatBundleConstituentsByFlightSelector,
  serviceCategoryOrderSelector,
  serviceCategoryTranslationsMapSelector,
} from '../../../store';
import { getPassengerAndInfantIds, routeLabel } from '../../../utils/shopping-cart.utils';

interface CartCategoryGroup {
  category: Category;
  items: CartLineItem[];
  categoryTitle?: string;
  bundleConstituentTitles: string[];
}

interface CartProductGroup {
  key: string;
  // Omitted for a direct bound, whose single flight is already named by the bound header.
  routeLabel?: string;
  categories: CartCategoryGroup[];
}

@Component({
  selector: 'fin-shopping-cart-bound-services',
  standalone: false,
  templateUrl: './shopping-cart-bound-services.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartBoundServicesComponent {
  private store$ = inject<Store<BookingAndAppState>>(Store);

  bound = input.required<FinnairBoundItem>();
  selectedPassengerId = input<string | null>(null);

  private cart = this.store$.selectSignal(cartDataSelector);
  protected currency = this.store$.selectSignal(boundsCurrencySelector);
  private localLineItems = this.store$.selectSignal(localCartLineItemsSelector);
  private categoryTranslations = this.store$.selectSignal(serviceCategoryTranslationsMapSelector);
  private bundleConstituents = this.store$.selectSignal(nonSeatBundleConstituentsByFlightSelector);
  private serviceCategoryOrder = this.store$.selectSignal(serviceCategoryOrderSelector);
  private cityNameByLocationCode = this.store$.selectSignal(cityNameByLocationCodeSelector);

  private flights = computed<FinnairItineraryItemFlight[]>(() => this.bound().itinerary.filter(isFlight));

  protected productGroups: Signal<CartProductGroup[]> = computed(() => {
    const bound = this.bound();
    const flights = this.flights();
    const passengerId = this.selectedPassengerId();
    const paxIds = passengerId ? new Set(getPassengerAndInfantIds(this.cart(), passengerId)) : null;
    const items = this.localLineItems().filter((item) => !item.isBenefit && (!paxIds || paxIds.has(item.paxId)));
    const isTransfer = flights.length > 1;
    const cityNames = this.cityNameByLocationCode();

    return [
      {
        key: bound.id,
        // All bundles are per flight, so the flight id used to resolve bundle copy is irrelevant here.
        categories: this.buildCategoryGroups(
          items.filter((item) => item.fragmentId === bound.id),
          flights[0]?.id
        ),
      },
      ...flights.map((flight) => ({
        key: flight.id,
        routeLabel: isTransfer ? routeLabel(flight, cityNames) : undefined,
        categories: this.buildCategoryGroups(
          items.filter((item) => item.fragmentId === flight.id),
          flight.id
        ),
      })),
    ].filter((group) => group.categories.length > 0);
  });

  private buildCategoryGroups(items: CartLineItem[], flightId: string | undefined): CartCategoryGroup[] {
    const categoryTranslations = this.categoryTranslations();
    const bundleConstituents = this.bundleConstituents();
    const categoryOrder = this.serviceCategoryOrder();
    return this.groupLineItemsByCategory(items)
      .sort(
        (a, b) =>
          this.categoryRank(a.category, categoryOrder) - this.categoryRank(b.category, categoryOrder) ||
          a.category.localeCompare(b.category)
      )
      .map(({ category, items: categoryItems }) => ({
        category,
        items: categoryItems,
        categoryTitle: categoryTranslations?.[category]?.title,
        bundleConstituentTitles: isAncillaryBundle(category)
          ? (bundleConstituents?.[category]?.[flightId] ?? []).map((service) => service.translations?.title ?? '')
          : [],
      }));
  }

  private groupLineItemsByCategory(items: CartLineItem[]): Array<{ category: Category; items: CartLineItem[] }> {
    const byCategory = new Map<Category, CartLineItem[]>();
    items.forEach((item) => {
      byCategory.set(item.topCategory, [...(byCategory.get(item.topCategory) ?? []), item]);
    });
    return Array.from(byCategory, ([category, categoryItems]) => ({ category, items: categoryItems })).filter(
      ({ items: categoryItems }) => categoryItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0) > 0
    );
  }

  // Try to match travel extras ordering
  private categoryRank(category: Category, catalogOrder: Category[]): number {
    const seatBundleIndex = SEAT_BUNDLE_CATEGORIES.indexOf(category);
    if (seatBundleIndex !== -1) {
      return seatBundleIndex;
    }
    const catalogIndex = catalogOrder.indexOf(category);
    return SEAT_BUNDLE_CATEGORIES.length + (catalogIndex === -1 ? catalogOrder.length : catalogIndex);
  }
}
